// Loose-file loader for an OGS character's inference bundle (POST
// /api/characters/{slug}/ogs). Lists the collection, fetches every file the
// scene needs, and mounts them into the in-memory gsPack VFS.
//
// ONE PATH FOR EVERY BUNDLE. There used to be two loaders here — a multi_region
// one keyed on scene.json, and a separate single-region one (schema_version 1:
// flat geom/appr/mesh, no scene.json) with its own file selection, its own
// concurrent fetch, its own lazy-fp32 logic and its own return shape. They did
// the same job and drifted: only the multi_region branch metered the boot
// progress bar, and the single-region
// branch built a `modelBuffers` object nothing had consumed since the node-path
// refactor.
//
// The render pipeline was ALREADY unified (one OGSBranch per region; a
// single-region bundle is one branch whose geom is FUSED — see
// ogs/assets/sceneManifest.ts's singleRegionScene). Only the LOADER was still
// forked, and the fork was purely about where the manifest comes from. So that
// is now the only difference: resolveSceneJson() either downloads scene.json or
// SYNTHESIZES the equivalent manifest, and everything downstream is identical.
//
// Loud failure, no silent fallback (CLAUDE.md UX rules): progress + errors land
// on window.__OGS_CONTENT_PACKS__.

import { listOgsAssets, fetchAamFile, cachedFetchAamFile } from './assetManagerApi.js'
import { mountFiles } from './gsPack.js'
import { selectMeshOnlyFiles, isRequiredFileSatisfied } from './ogsContentSelect.js'
import { resolveBundleManifest } from './ogsBundleManifest.js'
import {
  multiRegionFileList,
  multiRegionOptionalFileList,
  sceneNeedsRigDeform,
  rig2MeshFileNames,
  multiRegionRigParamsList,
  fp16NameFor,
} from '../ogs/assets/sceneManifest.ts'
import { resolveRigMode } from '../ogs/inference/rigMode.ts'
import { ogsGpuFlags } from '../ogs/inference/ogsGpuFlags.ts'
import { POSE_ANCHOR_FILES } from '../ogs/inference/poseAnchors.js'
import { orlBundleAssetNames, hasOrlBundle } from './orl/bundleFiles.js'
import { reportRigProblem, clearRigProblem } from './ogsAssetStatus.js'
import { assetLoadProgress } from './assetLoadProgress.js'
import { labelForAam } from './aamAssetCache.js'

const decode = (bytes) => new TextDecoder().decode(bytes)

// Light probe (no file downloads): classify the /ogs collection; returns the
// mesh-only selection ({ rigUrl, clips }) or null. OGSModel uses this at mount
// to route a GLB-only bundle to MeshClipPreview without touching the splat path.
export async function probeMeshOnlyBundle(slug) {
  return selectMeshOnlyFiles(await listOgsAssets(slug))
}

function setGlobal(patch) {
  if (typeof window === 'undefined') return
  window.__OGS_CONTENT_PACKS__ = { ...(window.__OGS_CONTENT_PACKS__ ?? { state: 'idle' }), ...patch }
}

// Proxy URL for a named file in an /ogs list ({ filename, fileUrl } rows), or null.
function rowUrl(rows, name) {
  const r = (rows ?? []).find((x) => (x.filename ?? x.name) === name)
  return r ? (r.fileUrl ?? r.file_url) : null
}

// Small json/npy reads during manifest resolution. Goes through the SAME
// Cache-Storage layer as the bulk downloads (size-versioned key) — the old
// single-region path cached mesh.json and losing that would mean a network hit
// on every warm reload for a file we already trust.
async function fetchJson(url, what, size) {
  const res = await cachedFetchAamFile(url, size)
  if (!res.ok) throw new Error(`/ogs fetch ${what}: HTTP ${res.status}`)
  return new Uint8Array(await res.arrayBuffer())
}

let _promise = null
let _slug = null

// Fetch + mount a character's /ogs bundle (once per slug; concurrent callers share
// the in-flight promise).
export function loadOgsContentPacks(slug) {
  if (_promise && _slug === slug) return _promise
  _slug = slug
  setGlobal({ state: 'pending', character: slug, error: null })
  _promise = (async () => {
    const rows = await listOgsAssets(slug)

    // Mesh-only bundle (rig.glb + clip GLBs, no inference assets): rendered by
    // MeshClipPreview via the mount-time probe — nothing to fetch or mount here.
    const meshOnly = selectMeshOnlyFiles(rows)
    if (meshOnly) {
      setGlobal({ state: 'applied', meshOnly: true, files: [...(rows ?? []).map((r) => r.filename ?? r.name)] })
      return { meshOnly: true, ...meshOnly }
    }

    const files = new Map()
    const sceneJson = await resolveBundleManifest({ slug, rows, files, fetchFile: fetchJson })

    // WHICH RIG drives this character's branches, decided once here because the
    // answer decides what gets downloaded.
    //
    // The head is driven by the EXACT MetaHuman rig — OpenRigLogic plus this
    // character's own DNA, shipped as ONE `orl_pack.bin` for the whole head.
    // The distilled per-branch rig2mesh ONNX it replaced (~74 MB EACH,
    // the largest thing in a bundle) is the FALLBACK: fetched only for a
    // character that ships no pack. Exactly one of the two is ever downloaded,
    // so keeping the fallback costs a character with a DNA nothing.
    const availableRows = new Set((rows ?? []).map((r) => r.filename ?? r.name).filter(Boolean))
    const rigMode = resolveRigMode({
      want: ogsGpuFlags().rig,
      hasPack: hasOrlBundle((n) => availableRows.has(n)),
      rig2mesh: rig2MeshFileNames(sceneJson).filter((b) => availableRows.has(b.file)),
      needsRig: sceneNeedsRigDeform(sceneJson),
      slug,
    })
    const orlEnabled = rigMode.rig === 'orl'
    // 'none' means this character's head cannot be posed at all: it renders at
    // the bundle's neutral pose. Say so in the UI — a motionless face is
    // otherwise indistinguishable from a hung decoder, and the fix (drop a
    // head.dna on it) is not guessable.
    //
    // Only for a bundle that actually needs a rig: a single-region bundle's
    // FUSED geom takes rig_params directly and declares no rig2mesh, so it
    // animates with no DNA and must not be warned about (sceneNeedsRigDeform).
    if (rigMode.rig === 'none' && rigMode.needsRig) {
      reportRigProblem(slug, { cause: rigMode.fix, detail: rigMode.why })
    } else {
      // Not a clean slate: a rig DID resolve, so anything the previous character
      // reported is stale. A calibration mismatch found later re-reports.
      clearRigProblem()
    }

    const names = multiRegionFileList(sceneJson)
    const sizeByRow = new Map((rows ?? []).map((r) => [r.filename ?? r.name, r.size ?? r.byte_size]))
    const fetchRow = (name) => cachedFetchAamFile(rowUrl(rows, name), sizeByRow.get(name))

    // Optional extras. Phase 3 part-drive assets: `rig.glb` (one skinned mesh per body
    // part over a single bone hierarchy) + the body clip every part binds by bone name.
    // No vertex_association.json — the branch mesh's own `faces` already names the trained
    // vertex each GLB corner belongs to, so the correspondence needs no precomputed file.
    // rig_names.json is opportunistic but carries the lip-sync wiring: OGSModel derives the
    // ARKit gather from it, and a bundle without it renders fine with an idle/neutral face.
    // No vis_mask_<branch>.bin: the per-branch hidden-texel cull is not fetched or
    // applied any more — a bundle may still ship one and it is simply not read.
    // The per-branch fp16-internal decoders halve the decode where the adapter
    // supports shader-f16; a bundle without them loads fp32 exactly as before.
    // `rig_params_<branch>.npy` (the scene's per-branch `rig_presets`) carries that
    // branch's REST POSE in row 0. It has to be RESIDENT, not lazy: loadMultiRegionScene
    // reads it through the pack mount, which answers 404 for a file that was only listed
    // — and the fallback for a 404 is an all-zero rig, which in a bone-transform control
    // space is no pose at all (the head decodes off-distribution and smears).
    const fp16Names = multiRegionOptionalFileList(sceneJson)
    // Both rigs are OPTIONAL bundle data, and the resolution above picked ONE:
    // the pack for a character that ships a DNA, its per-branch rig2mesh for one
    // that doesn't. Never both — that would be the ~74 MB per branch this
    // replaced, paid by every character.
    // `pose_anchors.json` carries the per-pose rig→checkpoint deltas
    // (inference/poseAnchors.js), baked offline by tools/bake_pose_anchors.mjs.
    // ~12 KB, and it must be RESIDENT for the same reason the rig params are:
    // OGSModel reads it through the pack mount right after calibrate.
    const optionalNames = ['rig.glb', 'rig_names.json', ...POSE_ANCHOR_FILES,
      ...multiRegionRigParamsList(sceneJson), ...fp16Names,
      ...(orlEnabled ? orlBundleAssetNames() : []), ...rigMode.files]

    // Required files must all be resolvable (loud, no silent fallback).
    // Report EVERY missing file, not just the first — a bundle uploaded with two
    // files short should say so in one go (the old single-region selector did).
    const availableNames = new Set((rows ?? []).map((r) => r.filename ?? r.name).filter(Boolean))
    const missing = names.filter((n) => !isRequiredFileSatisfied(n, availableNames, files.keys()))
    if (missing.length) {
      throw new Error(`/ogs bundle for '${slug}' missing: ${missing.join(', ')}`)
    }

    // A decoder whose fp16 sibling actually ships is fetched LAZILY: the fp32
    // bytes are only reached when the fp16 build fails, and eagerly pulling both
    // variants roughly doubles the ONNX payload for bytes no session is built
    // from. OGSBranch asks for them through `fetchBytes` if it ever needs them.
    const shippedFp16 = new Set(fp16Names.filter((n) => rowUrl(rows, n)))
    const lazyNames = new Set(names.filter((n) => {
      const sib = fp16NameFor(n)
      return sib && sib !== n && shippedFp16.has(sib)
    }))

    // Fetch through the Cache-Storage layer and CONCURRENTLY: these are the big
    // decoder/rig2mesh blobs (~300 MB for isaac V2), so each download meters the
    // boot progress bar and a warm reload serves them locally with no network.
    //
    // PRE-REGISTER every task (with its known row size) before awaiting any body.
    // cacheFirst otherwise registers a task only once its own fetch resolves, so
    // the progress denominator would grow file-by-file over the load — and any
    // moment where every registered-so-far task happened to be done reads as
    // 100% (the monotonic bar then latches there while the rest stream in).
    const rowsToFetch = [...names, ...optionalNames]
      .filter((name) => !files.has(name) && !lazyNames.has(name) && rowUrl(rows, name))
    for (const name of rowsToFetch) {
      assetLoadProgress.register(rowUrl(rows, name), labelForAam(name), sizeByRow.get(name) || 0)
    }
    const required = new Set(names)
    await Promise.all(rowsToFetch.map(async (name) => {
      const res = await fetchRow(name)
      if (!res.ok) {
        // Required file must exist; an optional 404 is fine (cachedFetchAamFile
        // already marked its task failed → dropped from the denominator).
        if (required.has(name)) throw new Error(`/ogs fetch ${name}: HTTP ${res.status}`)
        return
      }
      files.set(name, new Uint8Array(await res.arrayBuffer()))
    }))

    const texBase = mountFiles(slug, files)
    const rigNamesBytes = files.get('rig_names.json')

    setGlobal({
      state: 'applied',
      multiRegion: true,
      preferFp16: shippedFp16.size > 0,
      files: [...files.keys()],
    })

    return {
      texBase,
      multiRegion: true,
      scene: sceneJson,
      rigNames: rigNamesBytes ? JSON.parse(decode(rigNamesBytes)) : null,
      // Which rig the branches are built from, and whether a failed exact rig
      // may lazily pull its rig2mesh. OGSBranch.makeDeform consumes this rather
      // than re-deciding: only this layer knows what was actually downloaded.
      rigPlan: rigMode,
      getBytes: (name) => files.get(name),
      // Lazy download for the deliberately-unfetched files above (the fp32
      // fallback decoders). Loud on a missing row / HTTP error — a decoder that
      // can't build must fail visibly, never silently stub.
      fetchBytes: async (name) => {
        const have = files.get(name)
        if (have) return have
        if (!rowUrl(rows, name)) throw new Error(`/ogs bundle for '${slug}' missing: ${name}`)
        assetLoadProgress.register(rowUrl(rows, name), labelForAam(name), sizeByRow.get(name) || 0)
        const res = await fetchRow(name)
        if (!res.ok) throw new Error(`/ogs fetch ${name}: HTTP ${res.status}`)
        const bytes = new Uint8Array(await res.arrayBuffer())
        files.set(name, bytes)
        return bytes
      },
    }
  })().catch((e) => {
    setGlobal({ state: 'error', error: String(e?.message ?? e) })
    _promise = null // allow a retry on the next mount attempt
    throw e
  })
  return _promise
}
